import { useAppSelector } from "hooks/useAppSelector";
import { SeatSelectionScreenProps } from "navigation/types";
import { useFetchInventoryTypesQuery } from "queries/ticket.queries";
import { View, useWindowDimensions } from "react-native";
import StandardSeatSelectionFlow from "./StandardSeatSelectionFlow";
import PremiumSeatSelectionFlow from "./PremiumSeatSelectionFlow";
import InnerLoading from "components/InnerLoading";
import { useNavigation } from "@react-navigation/native";
import {
  NavigationState,
  SceneMap,
  SceneRendererProps,
  TabBar,
  TabView,
} from "react-native-tab-view";
import { useMemo, useState } from "react";
import {
  Text,
  TopNavigation,
  TopNavigationAction,
} from "@ui-kitten/components";
import { TouchableWebElement } from "@ui-kitten/components/devsupport";
import Chevron from "components/Chevron";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { formatEventDate } from "utils/eventUtils";

type RenderTabBarType = SceneRendererProps & {
  navigationState: NavigationState<{
    key: string;
    title: string;
  }>;
};

const SeatSelection: React.FC<SeatSelectionScreenProps> = () => {
  const { data: branding } = useFetchTenantBrandingQuery();
  const { selectedEvent, selectedSubscription, isChangingSeats } =
    useAppSelector((state) => state.reservationFlow);
  const navigation = useNavigation();
  const { data: inventoryTypes } = useFetchInventoryTypesQuery({
    planPackageId: selectedSubscription?.planPackage.id,
    eventId: selectedEvent?.eventId,
  });

  const layout = useWindowDimensions();
  const [index, setIndex] = useState(0);

  const renderScene = SceneMap({
    premiumFlow: PremiumSeatSelectionFlow,
    standardFlow: StandardSeatSelectionFlow,
  });

  const routes = useMemo(() => {
    const result = [];
    if (inventoryTypes?.containsPremium) {
      result.push({ key: "premiumFlow", title: "Premium Experiences" });
    }
    if (inventoryTypes?.containsStandard) {
      result.push({ key: "standardFlow", title: "Standard Seating" });
    }
    return result;
  }, [inventoryTypes]);

  const renderTabBar = (props: RenderTabBarType) => (
    <TabBar
      {...props}
      indicatorStyle={{ backgroundColor: "black" }}
      style={{ backgroundColor: "transparent" }}
      renderLabel={({ route }) => (
        <Text style={{ color: "black" }}>{route.title}</Text>
      )}
      tabStyle={{ backgroundColor: "transparent", minHeight: 30 }}
    />
  );
  const renderBackAction = (): TouchableWebElement => (
    <TopNavigationAction
      onPress={navigation.goBack}
      icon={<Chevron direction="left" color="white" />}
    />
  );

  if (!inventoryTypes) return <InnerLoading />;
  return (
    <View style={{ flex: 1 }}>
      <TopNavigation
        style={{ backgroundColor: branding?.primaryColor }}
        alignment="center"
        title={() => (
          <Text category="h6" status="control" numberOfLines={1}>
            {isChangingSeats ? "Updating Reservation: " : ""}
            {selectedEvent!.eventName}
          </Text>
        )}
        subtitle={() => (
          <Text category="c2" status="control">
            {formatEventDate(selectedEvent!)}
          </Text>
        )}
        accessoryLeft={renderBackAction}
      />
      {/* has both inventory types */}
      {routes.length > 1 ? (
        <TabView
          navigationState={{ index, routes }}
          renderScene={renderScene}
          onIndexChange={setIndex}
          initialLayout={{ width: layout.width }}
          renderTabBar={renderTabBar}
          swipeEnabled={false}
        />
      ) : inventoryTypes.containsPremium ? (
        <PremiumSeatSelectionFlow />
      ) : (
        <StandardSeatSelectionFlow />
      )}
    </View>
  );
};

export default SeatSelection;
